import React, { useEffect, useRef, useState } from "react";
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Button,
  Chip,
  ClickAwayListener,
  Drawer,
  IconButton,
  InputAdornment,
  List,
  ListItemAvatar,
  ListItemButton,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Stack,
  TextField,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import "../Styles/Navbar.css";
import logo from "../Images/LogoUK.jpg";
import DrawerComp from "./Drawer";

import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import GTranslateIcon from "@mui/icons-material/GTranslate";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useDispatch, useSelector } from "react-redux";
import { loadCurrentUser, removeFromCart, updateQuantity } from "../../store";

import { promoBrandLinks, fetchShopCategories } from "./navConfig";
import { NAV } from "./navbarTheme";
import { searchProducts, stopProductCatalogScan } from "../utils/shopApi";
import { usePendingQuotes } from "../hooks/usePendingQuotes";
import PendingQuoteItems from "../Components/PendingQuoteItems";

const PRODUCT_SEARCH_MIN_CHARS = 1;
const PRODUCT_SUGGEST_LIMIT = 10;
const GOOGLE_TRANSLATE_ELEMENT_ID = "google_translate_element";
const CATEGORY_SUBMENU_CLOSE_DELAY_MS = 300;
const CATEGORY_SUBMENU_HOVER_BRIDGE_PX = 12;

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const getCatalogProductImage = (product) => {
  if (product?.image_url) return product.image_url;
  if (typeof product?.image === "string" && product.image.startsWith("http")) {
    return product.image;
  }
  if (typeof product?.image === "string" && product.image) {
    return `https://api.uktomars.co.uk/storage/${product.image}`;
  }
  return "";
};

function ProductNameHighlight({ name, query }) {
  const text = String(name ?? "");
  const q = String(query ?? "").trim();
  if (!q || !text) return <>{text}</>;
  try {
    const re = new RegExp(`(${escapeRegExp(q)})`, "gi");
    const parts = text.split(re);
    const qLower = q.toLowerCase();
    return (
      <>
        {parts.map((part, i) =>
          part.toLowerCase() === qLower ? (
            <Box
              component="strong"
              key={`${i}-${part}`}
              sx={{ fontWeight: 800, color: NAV.navy }}
            >
              {part}
            </Box>
          ) : (
            <span key={`${i}-${part}`}>{part}</span>
          ),
        )}
      </>
    );
  } catch {
    return <>{text}</>;
  }
}

const getProductSearchCode = (product) =>
  String(product?.code || product?.sku || "").trim();

const getProductSearchBarcode = (product) =>
  String(product?.unit_barcode || "").trim();

function ProductSearchSuggestionText({ product, query, compact = false }) {
  const code = getProductSearchCode(product);
  const barcode = getProductSearchBarcode(product);

  return (
    <Box sx={{ minWidth: 0, textAlign: "left" }}>
      <Typography
        variant="body2"
        sx={{
          color: NAV.ink,
          fontSize: compact ? "0.75rem" : "0.8125rem",
          lineHeight: 1.35,
          fontWeight: 500,
          textAlign: "left",
        }}
      >
        <ProductNameHighlight name={product?.name ?? ""} query={query} />
      </Typography>
      {code || barcode ? (
        <Typography
          component="div"
          sx={{
            mt: 0.35,
            color: NAV.inkMuted,
            fontSize: compact ? "0.65rem" : "0.6875rem",
            lineHeight: 1.4,
            fontWeight: 500,
            wordBreak: "break-word",
          }}
        >
          {code ? (
            <Box
              component="span"
              sx={{ display: "inline", mr: barcode ? 1.25 : 0 }}
            >
              Product Code: <ProductNameHighlight name={code} query={query} />
            </Box>
          ) : null}
          {barcode ? (
            <Box component="span" sx={{ display: "inline" }}>
              Barcode: <ProductNameHighlight name={barcode} query={query} />
            </Box>
          ) : null}
        </Typography>
      ) : null}
    </Box>
  );
}

const navTextButtonSx = {
  color: NAV.ink,
  fontWeight: 600,
  fontSize: "0.9375rem",
  textTransform: "none",
  minWidth: "auto",
  px: 1.5,
  py: 0.75,
  letterSpacing: "0.02em",
  borderRadius: 1.5,
  transition: NAV.transition,
  "&:hover": {
    backgroundColor: NAV.navyMuted,
    color: NAV.navy,
  },
};

const iconButtonNavSx = {
  color: NAV.ink,
  flexShrink: 0,
  borderRadius: 2,
  transition: NAV.transition,
  "&:hover": {
    backgroundColor: NAV.navyMuted,
    color: NAV.navy,
  },
};

const searchFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 999,
    backgroundColor: NAV.surface,
    fontSize: "0.9375rem",
    pl: 2.25,
    pr: 0.5,
    transition: NAV.transition,
    "& .MuiInputAdornment-positionEnd": {
      display: "flex",
      alignItems: "center",
      gap: 0.25,
    },
    "& fieldset": {
      borderWidth: 1,
      borderColor: NAV.line,
    },
    "&:hover fieldset": {
      borderColor: NAV.inkMuted,
    },
    "&.Mui-focused": {
      backgroundColor: NAV.surface,
      boxShadow: `0 0 0 3px ${NAV.navyMuted}`,
    },
    "&.Mui-focused fieldset": {
      borderColor: NAV.navy,
      borderWidth: 1,
    },
  },
  "& .MuiOutlinedInput-input::placeholder": {
    color: NAV.inkMuted,
    opacity: 1,
  },
};

/** Wraps desktop search field — keeps pill width aligned with suggestion dropdown */
const searchFieldLaptopWrapperSx = {
  width: "100%",
  maxWidth: { md: 400, lg: 520, xl: 640 },
  mx: "auto",
  position: "relative",
};

/** Desktop / laptop: larger pill, subtle fill, lift on hover/focus */
const searchFieldLaptopSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    borderRadius: 999,
    minHeight: { md: 46, lg: 48 },
    backgroundColor: NAV.surfaceSubtle,
    fontSize: { md: "0.9375rem", lg: "1rem" },
    fontWeight: 500,
    pl: 0.5,
    pr: 0.35,
    transition: `${NAV.transition}, box-shadow 0.25s ease`,
    boxShadow: NAV.searchInset,
    "& fieldset": {
      borderWidth: 1,
      borderColor: NAV.line,
    },
    "&:hover": {
      backgroundColor: NAV.surface,
      boxShadow: NAV.searchHoverLift,
      "& fieldset": {
        borderColor: NAV.inkMuted,
      },
    },
    "&.Mui-focused": {
      backgroundColor: NAV.surface,
      boxShadow: NAV.searchFocusLift,
      "& fieldset": {
        borderColor: NAV.navy,
        borderWidth: 1.5,
      },
    },
    "& .MuiInputAdornment-positionEnd": {
      display: "flex",
      alignItems: "center",
      gap: 0.35,
      maxHeight: 40,
    },
  },
  "& .MuiOutlinedInput-input": {
    py: 1.125,
    letterSpacing: "0.01em",
  },
  "& .MuiOutlinedInput-input::placeholder": {
    color: NAV.inkMuted,
    opacity: 1,
    fontWeight: 400,
  },
};

const laptopSearchIconButtonSx = {
  color: NAV.navy,
  mr: 0.25,
  p: 1,
  borderRadius: 999,
  backgroundColor: NAV.navyMuted,
  transition: NAV.transition,
  "&:hover": {
    backgroundColor: NAV.navy,
    color: "#fff",
  },
};

const laptopSearchClearIconSx = {
  color: NAV.inkMuted,
  p: 0.65,
  mr: 0.15,
  borderRadius: 999,
  transition: NAV.transition,
  "&:hover": {
    color: NAV.navy,
    backgroundColor: NAV.navyMuted,
  },
};

/** Category pills on dark laptop row — outlined + solid fill overrides */
const categoryChipDesktopSx = {
  flexShrink: 0,
  height: "auto",
  minHeight: 36,
  borderRadius: 2.5,
  fontWeight: 600,
  textDecoration: "none",
  color: NAV.ink,
  bgcolor: NAV.categoryChipOnDark,
  borderColor: NAV.categoryChipBorderOnDark,
  boxShadow: NAV.categoryChipRestShadow,
  transition:
    "color 0.2s ease, background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease",
  "& .MuiChip-label": {
    px: 1.25,
    py: 0.65,
    whiteSpace: "normal",
    lineHeight: 1.3,
    textAlign: "center",
    fontSize: { md: "0.72rem", lg: "0.78rem" },
    fontWeight: 600,
    letterSpacing: "0.01em",
    color: "inherit",
  },
  "&.MuiChip-outlined": {
    backgroundColor: NAV.categoryChipOnDark,
  },
  "&:hover": {
    bgcolor: NAV.categoryChipHoverFill,
    backgroundColor: NAV.categoryChipHoverFill,
    borderColor: "rgba(255, 255, 255, 0.5)",
    color: "#ffffff",
    boxShadow: NAV.categoryChipShadowHover,
    transform: "translateY(-1px)",
    "& .MuiChip-label": {
      color: "#ffffff",
    },
  },
  "&:active": {
    bgcolor: NAV.categoryChipActiveFill,
    backgroundColor: NAV.categoryChipActiveFill,
    borderColor: "rgba(255, 255, 255, 0.55)",
    color: "#ffffff",
    boxShadow: NAV.categoryChipShadowActive,
    transform: "translateY(0)",
    "& .MuiChip-label": {
      color: "#ffffff",
    },
  },
  "&.Mui-focusVisible": {
    bgcolor: NAV.categoryChipHoverFill,
    borderColor: "rgba(255, 255, 255, 0.55)",
    color: "#ffffff",
    boxShadow: NAV.categoryChipShadowFocus,
    "& .MuiChip-label": {
      color: "#ffffff",
    },
  },
};

const Navbar = () => {
  const theme = useTheme();
  const isMatch = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  const [navbarScrolled, setNavbarScrolled] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const mobileSearchInputRef = useRef(null);
  const [anchorElCategory, setAnchorElCategory] = useState(null);
  const [activeCategory, setActiveCategory] = useState(null);
  const [shopCategories, setShopCategories] = useState([]);
  const categoryCloseTimerRef = useRef(null);
  const [cartDrawerOpen, setCartDrawerOpen] = useState(false);
  const [productSearchMatches, setProductSearchMatches] = useState([]);
  const [searchSuggestionsOpen, setSearchSuggestionsOpen] = useState(false);
  const [searchAnchorEl, setSearchAnchorEl] = useState(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const cartItems = useSelector((state) => state.cart.items);
  const authUser = useSelector((state) => state.auth.user);
  const authToken = useSelector((state) => state.auth.token);
  const isAuthenticated = Boolean(authToken);
  const cartItemCount = cartItems.reduce(
    (sum, entry) => sum + entry.quantity,
    0,
  );
  const {
    pendingQuotes,
    loading: pendingLoading,
    error: pendingError,
    setError: setPendingError,
    updateLineQuantity,
    removeLine,
    updatingKey,
    hasPendingQuotes,
  } = usePendingQuotes({ enabled: isAuthenticated });

  const handlePendingQuantityChange = async (quote, lineId, change) => {
    try {
      setPendingError("");
      await updateLineQuantity(quote, lineId, change);
    } catch (updateError) {
      setPendingError(
        updateError?.message || "Unable to update this quote line right now.",
      );
    }
  };

  const handlePendingLineRemove = async (quote, lineId) => {
    try {
      setPendingError("");
      await removeLine(quote, lineId);
    } catch (updateError) {
      setPendingError(
        updateError?.message || "Unable to remove this quote line right now.",
      );
    }
  };

  const clearCategoryCloseTimer = () => {
    if (categoryCloseTimerRef.current) {
      window.clearTimeout(categoryCloseTimerRef.current);
      categoryCloseTimerRef.current = null;
    }
  };

  const handleCategoryClose = () => {
    clearCategoryCloseTimer();
    setAnchorElCategory(null);
    setActiveCategory(null);
  };

  const scheduleCategoryClose = () => {
    clearCategoryCloseTimer();
    categoryCloseTimerRef.current = window.setTimeout(() => {
      handleCategoryClose();
    }, CATEGORY_SUBMENU_CLOSE_DELAY_MS);
  };

  const handleCategoryMouseEnter = (event, category) => {
    if (!category.subcategories?.length) return;
    clearCategoryCloseTimer();
    setAnchorElCategory(event.currentTarget);
    setActiveCategory(category);
  };

  const handleCategoryMouseLeave = () => {
    scheduleCategoryClose();
  };

  const handleCategoryMenuEnter = () => {
    clearCategoryCloseTimer();
  };

  const handleCategoryMenuLeave = () => {
    scheduleCategoryClose();
  };

  const handleCategoryClick = (_event, category) => {
    navigate({
      pathname: "/products",
      search: `?category=${encodeURIComponent(category.slug)}`,
    });
    handleCategoryClose();
  };

  useEffect(() => {
    if (authToken && !authUser) {
      dispatch(loadCurrentUser());
    }
  }, [authToken, authUser, dispatch]);

  useEffect(() => {
    setMobileSearchOpen(false);
  }, [location.pathname]);

  useEffect(() => () => clearCategoryCloseTimer(), []);

  useEffect(() => {
    if (!isMatch) setMobileSearchOpen(false);
  }, [isMatch]);

  useEffect(() => {
    if (mobileSearchOpen && isMatch) {
      const t = window.setTimeout(() => {
        mobileSearchInputRef.current?.focus?.();
      }, 50);
      return () => window.clearTimeout(t);
    }
  }, [mobileSearchOpen, isMatch]);

  useEffect(() => {
    const handleScroll = () => {
      setNavbarScrolled(window.scrollY > 6);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadCategories = async () => {
      try {
        const categories = await fetchShopCategories();
        if (isMounted) {
          setShopCategories(categories);
        }
      } catch (error) {
        if (isMounted) {
          setShopCategories([]);
        }
      }
    };

    loadCategories();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const q = searchQuery.trim();
    if (q.length < PRODUCT_SEARCH_MIN_CHARS) {
      setProductSearchMatches([]);
      stopProductCatalogScan();
      return undefined;
    }

    let isMounted = true;
    const abortController = new AbortController();
    const timer = window.setTimeout(async () => {
      try {
        const matches = await searchProducts(q, {
          perPage: PRODUCT_SUGGEST_LIMIT,
          signal: abortController.signal,
        });
        if (isMounted) {
          setProductSearchMatches(matches.slice(0, PRODUCT_SUGGEST_LIMIT));
        }
      } catch {
        if (isMounted) setProductSearchMatches([]);
      }
    }, 350);

    return () => {
      isMounted = false;
      abortController.abort();
      window.clearTimeout(timer);
    };
  }, [searchQuery]);

  useEffect(() => {
    if (isMatch && !mobileSearchOpen) {
      setSearchAnchorEl(null);
    }
  }, [isMatch, mobileSearchOpen]);

  useEffect(() => {
    const initTranslate = () => {
      const container = document.getElementById(GOOGLE_TRANSLATE_ELEMENT_ID);
      if (!container || container.childElementCount > 0) return;
      if (!window.google?.translate?.TranslateElement) return;

      new window.google.translate.TranslateElement(
        {
          pageLanguage: "en",
          autoDisplay: false,
        },
        GOOGLE_TRANSLATE_ELEMENT_ID,
      );
    };

    window.googleTranslateElementInit = initTranslate;

    const existingScript = document.getElementById("google-translate-script");
    if (existingScript) {
      initTranslate();
      return;
    }

    const script = document.createElement("script");
    script.id = "google-translate-script";
    script.src =
      "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
    script.async = true;
    document.body.appendChild(script);

    return () => {
      window.googleTranslateElementInit = undefined;
    };
  }, []);

  const showProductSuggestions =
    searchSuggestionsOpen &&
    productSearchMatches.length > 0 &&
    searchQuery.trim().length >= PRODUCT_SEARCH_MIN_CHARS;

  useEffect(() => {
    const path = location.pathname;
    const onCatalog =
      path === "/products" ||
      path === "/Search" ||
      /^\/Brands\/[^/]+\/products$/i.test(path);
    if (onCatalog) {
      setSearchQuery(new URLSearchParams(location.search).get("q") ?? "");
    }
  }, [location.pathname, location.search]);

  const submitSearch = () => {
    setSearchSuggestionsOpen(false);
    const q = searchQuery.trim();
    const path = location.pathname;

    const navigateWithMergedQ = (pathname, search) => {
      const next = new URLSearchParams(search);
      if (q) next.set("q", q);
      else next.delete("q");
      const qs = next.toString();
      navigate(qs ? `${pathname}?${qs}` : pathname, { replace: false });
    };

    if (path === "/products" || path === "/Search") {
      navigateWithMergedQ("/products", location.search);
      return;
    }
    if (/^\/Brands\/[^/]+\/products$/i.test(path)) {
      navigateWithMergedQ(path, location.search);
      return;
    }
    if (q) {
      navigate(`/products?q=${encodeURIComponent(q)}`);
    } else {
      navigate("/products");
    }
  };

  const submitMobileSearch = () => {
    submitSearch();
    setMobileSearchOpen(false);
  };

  const handlePickSearchProduct = (product) => {
    if (!product?.id) return;
    setSearchSuggestionsOpen(false);
    setMobileSearchOpen(false);
    navigate(`/products/${product.id}`, { state: { product } });
  };

  const handleCartDrawerClose = () => {
    setCartDrawerOpen(false);
  };

  return (
    <React.Fragment>
      <AppBar
        elevation={0}
        className="navbar-appbar"
        sx={{
          backgroundColor: navbarScrolled
            ? "rgba(255,255,255,0.98)"
            : NAV.surface,
          backdropFilter: navbarScrolled ? "blur(10px)" : "none",
          color: NAV.ink,
          fontWeight: 600,
          fontSize: "16px",
          transition:
            "box-shadow 0.3s ease, background-color 0.3s ease, border-color 0.3s ease",
          top: 0,
          left: 0,
          right: 0,
          position: "fixed",
          zIndex: theme.zIndex.appBar,
          boxShadow: navbarScrolled ? NAV.shadowBarScrolled : NAV.shadowBar,
          borderBottom: `1px solid ${
            navbarScrolled ? "rgba(15, 40, 71, 0.12)" : NAV.line
          }`,
          WebkitFontSmoothing: "antialiased",
          MozOsxFontSmoothing: "grayscale",
        }}
      >
        <Toolbar
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { md: 2, lg: 3 },
            flexWrap: "nowrap",
            minHeight: { xs: 56, md: 72 },
            py: { xs: 0.5, md: 1 },
            px: { xs: 1.5, md: 2, lg: 3 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { md: 1.5, lg: 2 },
              flexShrink: 0,
            }}
          >
            <Link to="/" style={{ textDecoration: "none", color: "inherit" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: isMatch ? { xs: 0.75, sm: 1 } : 1.5,
                  borderRadius: 2,
                  pr: { md: 0.5 },
                  transition: NAV.transition,
                  "&:hover": {
                    backgroundColor: NAV.navyMuted,
                  },
                }}
              >
                <Box
                  sx={{
                    width: isMatch ? 44 : 52,
                    height: isMatch ? 44 : 52,
                    flexShrink: 0,
                    borderRadius: 0,
                    bgcolor: "transparent",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    boxShadow: "none",
                  }}
                >
                  <img
                    src={logo}
                    alt=""
                    style={{
                      width: isMatch ? 36 : 44,
                      height: isMatch ? 36 : 44,
                      objectFit: "cover",
                      borderRadius: 4,
                    }}
                  />
                </Box>
                <Box
                  className="TextNavbar"
                  sx={{
                    fontWeight: 700,
                    fontSize: isMatch
                      ? { xs: "0.75rem", sm: "0.85rem" }
                      : { md: "1.05rem", lg: "1.1rem" },
                    letterSpacing: "0.06em",
                    color: NAV.navy,
                    lineHeight: 1.15,
                    whiteSpace: "nowrap",
                  }}
                >
                  UK TO MARS
                </Box>
              </Box>
            </Link>

            {!isMatch && (
              <>
                <Button component={Link} to="/ContactUs" sx={navTextButtonSx}>
                  Contact Us
                </Button>
                <Button component={Link} to="/Track" sx={navTextButtonSx}>
                  Track Shipment
                </Button>
              </>
            )}
          </Box>

          {isMatch ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.25,
                ml: "auto",
                flexShrink: 0,
              }}
            >
              <IconButton
                aria-label={
                  mobileSearchOpen ? "Close search field" : "Open search field"
                }
                aria-expanded={mobileSearchOpen}
                onClick={() => setMobileSearchOpen((open) => !open)}
                sx={iconButtonNavSx}
              >
                {mobileSearchOpen ? (
                  <CloseIcon fontSize="medium" />
                ) : (
                  <SearchIcon fontSize="medium" />
                )}
              </IconButton>
              <IconButton
                aria-label="Shopping cart"
                aria-controls="cart-sidebar"
                aria-haspopup="dialog"
                aria-expanded={cartDrawerOpen}
                onClick={() => setCartDrawerOpen(true)}
                sx={iconButtonNavSx}
              >
                <Badge
                  badgeContent={cartItemCount}
                  color="error"
                  invisible={cartItemCount === 0}
                  sx={{
                    "& .MuiBadge-badge": {
                      fontSize: "0.65rem",
                      minWidth: 18,
                      height: 18,
                    },
                  }}
                >
                  <ShoppingCartOutlinedIcon fontSize="medium" />
                </Badge>
              </IconButton>
              <Box
                sx={{
                  ml: 0.25,
                  display: "flex",
                  alignItems: "center",
                  "& .goog-te-gadget": {
                    fontSize: 0,
                    lineHeight: 0,
                  },
                  "& .goog-te-combo, & select.goog-te-combo": {
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    opacity: 0,
                    cursor: "pointer",
                  },
                  "& .goog-logo-link, & .goog-te-gadget span": {
                    display: "none !important",
                  },
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    width: 34,
                    height: 34,
                    borderRadius: "50%",
                    border: `1px solid rgba(15, 40, 71, 0.18)`,
                    bgcolor: "rgba(255,255,255,0.95)",
                    boxShadow: "0 4px 12px rgba(15, 40, 71, 0.08)",
                    transition:
                      "background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease",
                    "&:hover": {
                      bgcolor: NAV.navyMuted,
                      borderColor: "rgba(15, 40, 71, 0.28)",
                      boxShadow: "0 8px 18px rgba(15, 40, 71, 0.14)",
                    },
                    "&:hover .translate-icon-mobile": {
                      color: NAV.navy,
                    },
                  }}
                >
                  <IconButton
                    size="small"
                    aria-label="Open translation languages"
                    sx={{
                      pointerEvents: "none",
                      color: NAV.inkMuted,
                      width: "100%",
                      height: "100%",
                    }}
                  >
                    <GTranslateIcon
                      className="translate-icon-mobile"
                      sx={{
                        fontSize: 19,
                        flexShrink: 0,
                        transition: "color 0.2s ease",
                      }}
                    />
                  </IconButton>
                  <Box
                    id={GOOGLE_TRANSLATE_ELEMENT_ID}
                    sx={{
                      position: "absolute",
                      inset: 0,
                      zIndex: 2,
                    }}
                  />
                </Box>
              </Box>
              <DrawerComp />
            </Box>
          ) : (
            <>
              <ClickAwayListener
                onClickAway={() => setSearchSuggestionsOpen(false)}
              >
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    justifyContent: "center",
                    minWidth: 0,
                    px: { md: 1, lg: 2 },
                  }}
                >
                  <Box
                    ref={(el) => {
                      if (!isMatch) setSearchAnchorEl(el);
                    }}
                    sx={searchFieldLaptopWrapperSx}
                  >
                    <Box
                      component="form"
                      onSubmit={(e) => {
                        e.preventDefault();
                        submitSearch();
                      }}
                      sx={{ width: "100%" }}
                    >
                      <TextField
                        name="q"
                        type="text"
                        inputMode="search"
                        autoComplete="off"
                        placeholder="Search..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onFocus={() => setSearchSuggestionsOpen(true)}
                        inputProps={{
                          "aria-label":
                            "Search products by name, code or barcode",
                          "aria-autocomplete": "list",
                          "aria-controls": "navbar-product-search-listbox",
                        }}
                        size="medium"
                        fullWidth
                        sx={searchFieldLaptopSx}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment
                              position="start"
                              sx={{ ml: 0.75, mr: -0.25 }}
                            >
                              <SearchIcon
                                sx={{
                                  color: NAV.inkMuted,
                                  fontSize: { md: 22, lg: 24 },
                                }}
                              />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end" sx={{ gap: 0.25 }}>
                              {searchQuery ? (
                                <IconButton
                                  type="button"
                                  aria-label="Clear search"
                                  edge="end"
                                  size="small"
                                  sx={laptopSearchClearIconSx}
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => {
                                    setSearchQuery("");
                                    setSearchSuggestionsOpen(false);
                                  }}
                                >
                                  <CloseIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              ) : null}
                              <IconButton
                                type="submit"
                                aria-label="Search"
                                edge="end"
                                size="medium"
                                sx={laptopSearchIconButtonSx}
                              >
                                <SearchIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>
                    <Popper
                      id="navbar-product-search-popper"
                      open={
                        showProductSuggestions &&
                        Boolean(searchAnchorEl) &&
                        !isMatch
                      }
                      anchorEl={searchAnchorEl}
                      placement="bottom-start"
                      disablePortal
                      modifiers={[
                        { name: "offset", options: { offset: [0, 8] } },
                      ]}
                      sx={{
                        zIndex: (t) => t.zIndex.modal,
                        width: searchAnchorEl
                          ? `${searchAnchorEl.clientWidth}px`
                          : undefined,
                      }}
                    >
                      <Paper
                        id="navbar-product-search-listbox"
                        elevation={8}
                        sx={{
                          mt: 0.5,
                          borderRadius: 2,
                          border: `1px solid ${NAV.line}`,
                          maxHeight: 360,
                          overflow: "auto",
                          boxShadow: NAV.shadowMenu,
                        }}
                      >
                        <List
                          dense
                          disablePadding
                          role="listbox"
                          aria-label="Product suggestions"
                        >
                          {productSearchMatches.map((product) => (
                            <ListItemButton
                              key={product.id}
                              role="option"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => handlePickSearchProduct(product)}
                              sx={{
                                alignItems: "flex-start",
                                py: 1.25,
                                px: 1.5,
                                borderBottom: `1px solid ${NAV.line}`,
                                "&:last-of-type": { borderBottom: "none" },
                                "&:hover": { bgcolor: NAV.navyMuted },
                              }}
                            >
                              <ListItemAvatar sx={{ minWidth: 56, mt: 0.25 }}>
                                <Avatar
                                  variant="rounded"
                                  src={getCatalogProductImage(product)}
                                  alt=""
                                  sx={{
                                    width: 48,
                                    height: 48,
                                    bgcolor: "#f1f5f9",
                                    "& img": { objectFit: "contain" },
                                  }}
                                />
                              </ListItemAvatar>
                              <ProductSearchSuggestionText
                                product={product}
                                query={searchQuery}
                              />
                            </ListItemButton>
                          ))}
                        </List>
                      </Paper>
                    </Popper>
                  </Box>
                </Box>
              </ClickAwayListener>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: { md: 0.25, lg: 0.25 },
                  flexShrink: 0,
                }}
              >
                {promoBrandLinks.map((item) => (
                  <Button
                    key={item.to}
                    component={Link}
                    to={item.to}
                    sx={navTextButtonSx}
                  >
                    {item.label}
                  </Button>
                ))}
                {isAuthenticated ? (
                  <Button component={Link} to="/Profile" sx={navTextButtonSx}>
                    Profile
                  </Button>
                ) : (
                  <>
                    <Button component={Link} to="/SignIn" sx={navTextButtonSx}>
                      Sign in
                    </Button>
                    <Button
                      component={Link}
                      to="/Register"
                      sx={navTextButtonSx}
                    >
                      Register
                    </Button>
                  </>
                )}
                <IconButton
                  aria-label="Shopping cart"
                  aria-controls="cart-sidebar"
                  aria-haspopup="dialog"
                  aria-expanded={cartDrawerOpen}
                  onClick={() => setCartDrawerOpen(true)}
                  sx={{ ...iconButtonNavSx, ml: 0.25 }}
                >
                  <Badge
                    badgeContent={cartItemCount}
                    color="error"
                    invisible={cartItemCount === 0}
                    sx={{
                      "& .MuiBadge-badge": {
                        fontSize: "0.65rem",
                        minWidth: 18,
                        height: 18,
                      },
                    }}
                  >
                    <ShoppingCartOutlinedIcon />
                  </Badge>
                </IconButton>
                <Box
                  sx={{
                    ml: 0.5,
                    display: "flex",
                    alignItems: "center",
                    "& .goog-te-gadget": {
                      fontSize: 0,
                      lineHeight: 0,
                    },
                    "& .goog-te-combo, & select.goog-te-combo": {
                      position: "absolute",
                      inset: 0,
                      width: "100%",
                      height: "100%",
                      opacity: 0,
                      cursor: "pointer",
                    },
                    "& .goog-logo-link, & .goog-te-gadget span": {
                      display: "none !important",
                    },
                  }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      width: 34,
                      height: 34,
                      borderRadius: "50%",
                      border: `1px solid rgba(15, 40, 71, 0.18)`,
                      bgcolor: "rgba(255,255,255,0.95)",
                      boxShadow: "0 4px 12px rgba(15, 40, 71, 0.08)",
                      transition:
                        "background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease",
                      "&:hover": {
                        bgcolor: NAV.navyMuted,
                        borderColor: "rgba(15, 40, 71, 0.28)",
                        boxShadow: "0 8px 18px rgba(15, 40, 71, 0.14)",
                      },
                      "&:hover .translate-icon": {
                        color: NAV.navy,
                      },
                    }}
                  >
                    <IconButton
                      size="small"
                      aria-label="Open translation languages"
                      sx={{
                        pointerEvents: "none",
                        color: NAV.inkMuted,
                        width: "100%",
                        height: "100%",
                      }}
                    >
                      <GTranslateIcon
                        className="translate-icon"
                        sx={{
                          fontSize: 19,
                          flexShrink: 0,
                          transition: "color 0.2s ease",
                        }}
                      />
                    </IconButton>
                    <Box
                      id={GOOGLE_TRANSLATE_ELEMENT_ID}
                      sx={{
                        position: "absolute",
                        inset: 0,
                        zIndex: 2,
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </>
          )}
        </Toolbar>

        {isMatch && mobileSearchOpen && (
          <Toolbar
            variant="dense"
            sx={{
              px: 1.5,
              py: 1,
              alignItems: "flex-start",
              flexWrap: "nowrap",
              borderTop: `1px solid ${NAV.line}`,
              bgcolor: NAV.chipRowBg,
            }}
          >
            <ClickAwayListener
              onClickAway={() => setSearchSuggestionsOpen(false)}
            >
              <Box
                component="form"
                onSubmit={(e) => {
                  e.preventDefault();
                  submitMobileSearch();
                }}
                sx={{
                  flex: 1,
                  display: "flex",
                  gap: 1,
                  minWidth: 0,
                  alignItems: "flex-start",
                }}
              >
                <Box
                  ref={(el) => {
                    if (isMatch && mobileSearchOpen) setSearchAnchorEl(el);
                  }}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <TextField
                    inputRef={mobileSearchInputRef}
                    autoFocus
                    name="q"
                    type="text"
                    inputMode="search"
                    autoComplete="off"
                    placeholder="Search products, Product Code or Barcode"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => setSearchSuggestionsOpen(true)}
                    inputProps={{
                      "aria-label":
                        "Search products by Name, Product Code or Barcode",
                      "aria-autocomplete": "list",
                      "aria-controls": "navbar-product-search-listbox-mobile",
                    }}
                    size="small"
                    fullWidth
                    sx={{ ...searchFieldSx, minWidth: 0 }}
                    InputProps={{
                      endAdornment: searchQuery ? (
                        <InputAdornment position="end">
                          <IconButton
                            type="button"
                            aria-label="Clear search"
                            edge="end"
                            size="small"
                            sx={laptopSearchClearIconSx}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                              setSearchQuery("");
                              setSearchSuggestionsOpen(false);
                            }}
                          >
                            <CloseIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </InputAdornment>
                      ) : null,
                    }}
                  />
                  <Popper
                    id="navbar-product-search-popper-mobile"
                    open={
                      showProductSuggestions &&
                      Boolean(searchAnchorEl) &&
                      isMatch &&
                      mobileSearchOpen
                    }
                    anchorEl={searchAnchorEl}
                    placement="bottom-start"
                    disablePortal
                    modifiers={[
                      { name: "offset", options: { offset: [0, 8] } },
                    ]}
                    sx={{
                      zIndex: (t) => t.zIndex.modal,
                      width: searchAnchorEl
                        ? `${searchAnchorEl.clientWidth}px`
                        : undefined,
                    }}
                  >
                    <Paper
                      id="navbar-product-search-listbox-mobile"
                      elevation={8}
                      sx={{
                        mt: 0.5,
                        borderRadius: 2,
                        border: `1px solid ${NAV.line}`,
                        maxHeight: 280,
                        overflow: "auto",
                        boxShadow: NAV.shadowMenu,
                      }}
                    >
                      <List
                        dense
                        disablePadding
                        role="listbox"
                        aria-label="Product suggestions"
                      >
                        {productSearchMatches.map((product) => (
                          <ListItemButton
                            key={product.id}
                            role="option"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => handlePickSearchProduct(product)}
                            sx={{
                              alignItems: "flex-start",
                              py: 1.1,
                              px: 1.25,
                              borderBottom: `1px solid ${NAV.line}`,
                              "&:last-of-type": { borderBottom: "none" },
                              "&:hover": { bgcolor: NAV.navyMuted },
                            }}
                          >
                            <ListItemAvatar sx={{ minWidth: 52, mt: 0.25 }}>
                              <Avatar
                                variant="rounded"
                                src={getCatalogProductImage(product)}
                                alt=""
                                sx={{
                                  width: 44,
                                  height: 44,
                                  bgcolor: "#f1f5f9",
                                  "& img": { objectFit: "contain" },
                                }}
                              />
                            </ListItemAvatar>
                            <ProductSearchSuggestionText
                              product={product}
                              query={searchQuery}
                              compact
                            />
                          </ListItemButton>
                        ))}
                      </List>
                    </Paper>
                  </Popper>
                </Box>
                <IconButton
                  type="submit"
                  aria-label="Search"
                  sx={{
                    ...iconButtonNavSx,
                    bgcolor: NAV.navy,
                    color: "#fff",
                    mt: 0.25,
                    flexShrink: 0,
                    "&:hover": {
                      bgcolor: "#0a1f38",
                      color: "#fff",
                    },
                  }}
                >
                  <SearchIcon fontSize="small" />
                </IconButton>
              </Box>
            </ClickAwayListener>
          </Toolbar>
        )}

        {!isMatch && (
          <Toolbar
            variant="dense"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexWrap: "nowrap",
              minHeight: { md: 52, lg: 56 },
              bgcolor: NAV.categoryRowBg,
              borderTop: "1px solid rgba(255, 255, 255, 0.12)",
              boxShadow: "inset 0 1px 0 rgba(255, 255, 255, 0.06)",
              py: { md: 1.125, lg: 1.25 },
              px: { md: 2, lg: 3 },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: { md: 0.875, lg: 1 },
                flex: 1,
                width: "100%",
                minWidth: 0,
                overflowX: "auto",
                overflowY: "hidden",
                py: 0.125,
                scrollbarWidth: "thin",
                scrollbarColor: `rgba(255, 255, 255, 0.28) transparent`,
                WebkitOverflowScrolling: "touch",
                "&::-webkit-scrollbar": { height: 6 },
                "&::-webkit-scrollbar-track": {
                  background: "rgba(255,255,255,0.04)",
                  borderRadius: 99,
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(255, 255, 255, 0.28)",
                  borderRadius: 99,
                  border: "2px solid transparent",
                  backgroundClip: "padding-box",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.42)",
                },
              }}
            >
              {shopCategories.map((cat) => {
                const hasSubs = Boolean(cat.subcategories?.length);
                const menuOpen =
                  hasSubs &&
                  activeCategory?.slug === cat.slug &&
                  Boolean(anchorElCategory);
                const chip = (
                  <Chip
                    label={
                      <Stack
                        direction="row"
                        alignItems="center"
                        component="span"
                        spacing={0.35}
                        sx={{ py: 0 }}
                        fontSize={{ md: "0.875rem", lg: "0.8125rem" }}
                        transition="none"
                      >
                        <Box component="span">{cat.label}</Box>
                        {/* {hasSubs ? (
                          <ArrowDropDownIcon
                            sx={{
                              fontSize: { md: 14, lg: 15 },
                              transition: "transform 0.2s ease",
                              transform: menuOpen
                                ? "rotate(180deg)"
                                : "rotate(0deg)",
                              opacity: 0.9,
                              flexShrink: 0,
                              display: "block",
                            }}
                            aria-hidden
                          />
                        ) : null} */}
                      </Stack>
                    }
                    onClick={(event) => handleCategoryClick(event, cat)}
                    clickable
                    size="medium"
                    sx={{
                      ...categoryChipDesktopSx,
                      minHeight: 32,
                      "& .MuiChip-label": {
                        ...categoryChipDesktopSx["& .MuiChip-label"],
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        py: 0.45,
                        px: 1,
                        fontSize: { md: "0.68rem", lg: "0.72rem" },
                      },
                    }}
                    variant="outlined"
                    aria-expanded={hasSubs ? menuOpen : undefined}
                    aria-haspopup={hasSubs ? "menu" : undefined}
                    aria-controls={
                      hasSubs && menuOpen ? "category-submenu" : undefined
                    }
                  />
                );

                if (!hasSubs) {
                  return <React.Fragment key={cat.slug}>{chip}</React.Fragment>;
                }

                return (
                  <Box
                    key={cat.slug}
                    onMouseEnter={(event) =>
                      handleCategoryMouseEnter(event, cat)
                    }
                    onMouseLeave={handleCategoryMouseLeave}
                    sx={{ display: "inline-flex", flexShrink: 0 }}
                  >
                    {chip}
                    <Popper
                      id={`category-submenu-${cat.slug}`}
                      open={menuOpen}
                      anchorEl={anchorElCategory}
                      placement="bottom-start"
                      modifiers={[
                        {
                          name: "offset",
                          options: { offset: [0, 2] },
                        },
                        {
                          name: "preventOverflow",
                          options: { padding: 8 },
                        },
                      ]}
                      sx={{ zIndex: (t) => t.zIndex.modal }}
                    >
                      <Paper
                        elevation={0}
                        onMouseEnter={handleCategoryMenuEnter}
                        onMouseLeave={handleCategoryMenuLeave}
                        sx={{
                          minWidth: 260,
                          borderRadius: 2.5,
                          border: `1px solid ${NAV.categorySubmenuBorder}`,
                          boxShadow: NAV.categorySubmenuShadow,
                          py: 0.5,
                          px: 0.5,
                          overflow: "visible",
                          bgcolor: NAV.categorySubmenuBg,
                          backgroundImage: `linear-gradient(180deg, #ffffff 0%, ${NAV.categorySubmenuBg} 100%)`,
                          position: "relative",
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            top: -CATEGORY_SUBMENU_HOVER_BRIDGE_PX,
                            left: 0,
                            right: 0,
                            height: CATEGORY_SUBMENU_HOVER_BRIDGE_PX,
                          },
                        }}
                      >
                        <Box sx={{ overflow: "hidden", borderRadius: 2.5 }}>
                          <MenuList
                            autoFocus={false}
                            autoFocusItem={false}
                            disablePadding
                          >
                            {cat.subcategories.map((subcat, idx, arr) => (
                              <MenuItem
                                key={`${cat.slug}-${subcat.slug}`}
                                component={Link}
                                to={`/products?category=${encodeURIComponent(
                                  cat.slug,
                                )}&subcategory=${encodeURIComponent(subcat.slug)}`}
                                onClick={handleCategoryClose}
                                sx={{
                                  fontSize: "0.8125rem",
                                  fontWeight: 600,
                                  py: 1.2,
                                  px: 1.5,
                                  mx: 0,
                                  my: 0,
                                  minHeight: 46,
                                  borderRadius: 1.75,
                                  color: NAV.ink,
                                  bgcolor: "transparent",
                                  borderBottom:
                                    idx < arr.length - 1
                                      ? `1px solid rgba(15, 40, 71, 0.08)`
                                      : "none",
                                  transition:
                                    "background-color 0.2s ease, color 0.2s ease, padding 0.2s ease, box-shadow 0.2s ease",
                                  letterSpacing: "0.015em",
                                  justifyContent: "space-between",
                                  gap: 1,
                                  "&:hover": {
                                    backgroundColor:
                                      NAV.categorySubmenuRowHover,
                                    color: NAV.navy,
                                    pl: 1.75,
                                    boxShadow:
                                      "inset 3px 0 0 0 rgba(15, 40, 71, 0.85)",
                                  },
                                  "&:hover svg": {
                                    color: NAV.navy,
                                    opacity: 1,
                                  },
                                  "&:focus-visible": {
                                    outline: `2px solid ${NAV.navy}`,
                                    outlineOffset: 2,
                                  },
                                }}
                              >
                                <Box
                                  component="span"
                                  sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    textAlign: "left",
                                  }}
                                >
                                  {subcat.label}
                                </Box>
                                <ChevronRightIcon
                                  sx={{
                                    fontSize: 18,
                                    color: NAV.inkMuted,
                                    opacity: 0.65,
                                    flexShrink: 0,
                                    transition:
                                      "color 0.2s ease, opacity 0.2s ease",
                                  }}
                                  aria-hidden
                                />
                              </MenuItem>
                            ))}
                          </MenuList>
                        </Box>
                      </Paper>
                    </Popper>
                  </Box>
                );
              })}
            </Box>
          </Toolbar>
        )}
      </AppBar>
      <Drawer
        id="cart-sidebar"
        anchor="right"
        open={cartDrawerOpen}
        onClose={handleCartDrawerClose}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: "rgba(15, 40, 71, 0.42)",
              backdropFilter: "blur(4px)",
            },
          },
        }}
        PaperProps={{
          onClick: (event) => {
            if (event.target.closest("a[href]")) {
              handleCartDrawerClose();
            }
          },
          sx: {
            width: { xs: "100%", sm: 400 },
            maxWidth: "100vw",
            display: "flex",
            flexDirection: "column",
            bgcolor: NAV.surface,
            borderLeft: `1px solid ${NAV.line}`,
            boxShadow: "-12px 0 48px rgba(15, 40, 71, 0.18)",
          },
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            px: 2,
            py: 1.75,
            background: `linear-gradient(135deg, ${NAV.navy} 0%, #163a5e 55%, #1a4570 100%)`,
            color: "#fff",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1,
            boxShadow: "inset 0 -1px 0 rgba(255,255,255,0.12)",
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              component="h2"
              sx={{
                fontWeight: 800,
                fontSize: "1.125rem",
                letterSpacing: "0.02em",
                lineHeight: 1.2,
              }}
            >
              Shopping cart
            </Typography>
            <Box
              sx={{
                fontSize: "0.8125rem",
                opacity: 0.88,
                mt: 0.35,
                fontWeight: 500,
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              {cartItemCount === 0 && !hasPendingQuotes ? (
                <Typography
                  component="span"
                  sx={{ fontSize: "inherit", fontWeight: "inherit" }}
                >
                  No items yet
                </Typography>
              ) : (
                <>
                  {hasPendingQuotes && pendingQuotes.length === 1 ? (
                    <Typography
                      component="span"
                      sx={{
                        fontSize: "inherit",
                        fontWeight: 700,
                        color: "#fff",
                      }}
                    >
                      Requested Quotes
                    </Typography>
                  ) : hasPendingQuotes ? (
                    <Typography
                      component="span"
                      sx={{ fontSize: "inherit", fontWeight: "inherit" }}
                    >
                      Active quotes
                    </Typography>
                  ) : null}
                  {hasPendingQuotes && cartItemCount > 0 ? (
                    <Typography
                      component="span"
                      sx={{ fontSize: "inherit", fontWeight: "inherit" }}
                    >
                      ·
                    </Typography>
                  ) : null}
                  {cartItemCount > 0 ? (
                    <Typography
                      component="span"
                      sx={{ fontSize: "inherit", fontWeight: "inherit" }}
                    >
                      {cartItemCount} {cartItemCount === 1 ? "item" : "items"}
                    </Typography>
                  ) : null}
                </>
              )}
            </Box>
          </Box>
          <IconButton
            aria-label="Close cart"
            onClick={handleCartDrawerClose}
            sx={{
              color: "#fff",
              bgcolor: "rgba(255,255,255,0.12)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.22)" },
            }}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            px: 2,
            py: 1.5,
            bgcolor: NAV.chipRowBg,
          }}
        >
          {cartItems.length === 0 && !pendingLoading ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                py: 6,
                px: 2,
              }}
            >
              <ShoppingCartOutlinedIcon
                sx={{ fontSize: 56, color: NAV.inkMuted, opacity: 0.5, mb: 2 }}
              />
              <Typography
                sx={{
                  color: NAV.ink,
                  fontWeight: 700,
                  fontSize: "1rem",
                  mb: 0.75,
                }}
              >
                Your cart is empty
              </Typography>
              <Typography
                sx={{
                  color: NAV.inkMuted,
                  fontSize: "0.875rem",
                  lineHeight: 1.5,
                  maxWidth: 280,
                }}
              >
                Browse the shop and tap “Add cart” to see products here.
              </Typography>
            </Box>
          ) : (
            cartItems.map((item) => (
              <Box
                key={item.id}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.25,
                  py: 1.5,
                  px: 1.25,
                  mb: 1.25,
                  borderRadius: 2,
                  bgcolor: NAV.surface,
                  border: `1px solid ${NAV.line}`,
                  boxShadow: "0 2px 8px rgba(15, 40, 71, 0.06)",
                  "&:last-child": { mb: 0 },
                }}
              >
                <Box
                  component="img"
                  src={item.image_url || item.image || ""}
                  alt=""
                  sx={{
                    width: 52,
                    height: 52,
                    objectFit: "contain",
                    borderRadius: 1.25,
                    border: `1px solid ${NAV.line}`,
                    bgcolor: NAV.surfaceSubtle,
                    flexShrink: 0,
                  }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.8125rem",
                      color: NAV.ink,
                      lineHeight: 1.35,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {item.name}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      mt: 1,
                    }}
                  >
                    <IconButton
                      size="small"
                      aria-label="Decrease quantity"
                      onClick={() =>
                        dispatch(
                          updateQuantity({ itemId: item.id, change: -1 }),
                        )
                      }
                      sx={{
                        border: `1px solid ${NAV.line}`,
                        borderRadius: 1,
                        p: 0.35,
                        bgcolor: NAV.surfaceSubtle,
                      }}
                    >
                      <RemoveIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                    <Typography
                      sx={{
                        fontSize: "0.8125rem",
                        fontWeight: 700,
                        minWidth: 24,
                        textAlign: "center",
                        color: NAV.navy,
                      }}
                    >
                      {item.quantity}
                    </Typography>
                    <IconButton
                      size="small"
                      aria-label="Increase quantity"
                      onClick={() =>
                        dispatch(updateQuantity({ itemId: item.id, change: 1 }))
                      }
                      sx={{
                        border: `1px solid ${NAV.line}`,
                        borderRadius: 1,
                        p: 0.35,
                        bgcolor: NAV.surfaceSubtle,
                      }}
                    >
                      <AddIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      aria-label="Remove item"
                      onClick={() => dispatch(removeFromCart(item.id))}
                      sx={{ ml: "auto", color: NAV.inkMuted }}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            ))
          )}
        </Box>

        <Box
          sx={{
            flexShrink: 0,
            p: 2,
            pt: 1.5,
            borderTop: `1px solid ${NAV.line}`,
            bgcolor: NAV.surface,
            boxShadow: "0 -8px 24px rgba(15, 40, 71, 0.06)",
          }}
        >
          <Button
            component={Link}
            to="/Cart"
            fullWidth
            variant="contained"
            disabled={cartItemCount === 0}
            onClick={handleCartDrawerClose}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 2,
              py: 1.15,
              fontSize: "0.875rem",
              background: `linear-gradient(90deg, ${NAV.navy} 0%, #0a1f38 100%)`,
              boxShadow: "0 8px 20px rgba(15, 40, 71, 0.25)",
              "&:hover": {
                background: `linear-gradient(90deg, #0a1f38 0%, #071426 100%)`,
                boxShadow: "0 10px 28px rgba(15, 40, 71, 0.3)",
              },
              "&.Mui-disabled": {
                background: NAV.line,
                color: NAV.inkMuted,
              },
            }}
          >
            View basket
          </Button>
          <Box sx={{ mt: 1.5 }}>
            <PendingQuoteItems
              quotes={pendingQuotes}
              variant="compact"
              loading={pendingLoading}
              error={pendingError}
              updatingKey={updatingKey}
              onUpdateQuantity={handlePendingQuantityChange}
              onRemoveLine={handlePendingLineRemove}
            />
          </Box>
        </Box>
      </Drawer>
      <Outlet />
    </React.Fragment>
  );
};

export default Navbar;
